import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {RegistrationStructuredTableRowMetadata} from 'notebook/integrations/registry/RegistrationTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  convertTagToTypedValue,
  formatTypedValue,
  getTypedValueFromBioentitySummary,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import * as RegistryIdentifierUtil from 'registry/RegistryIdentifierUtil';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {getBasesValueFromBioentitySummary} from '../bases-columns/BasesColumnConstants';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {getTopologyValueFromBioentitySummary} from '../columns/TopologyColumnParsers';
import {LiveCellValue} from '../LiveCellValues';
import {
  BasesStructuredTableColumn,
  DirectLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from '../StructuredTableConstants';
import {
  BulkBioentityLiveDataQuery,
  BulkBioentityLiveDataQueryVariables,
} from './__generated/BulkBioentityLiveDataQuery';
import {buildStandardUpdatedCells} from './updateRowUtils';

// exported for testing
export type RegisteredEntityInfo = ExcludeInaccessible<
  BulkBioentityLiveDataQuery['directBioentities'][number]
>;

// Agent keep in sync: the server-side resync fetch fetch_bioentity_live_data in
// benchling/notebook/structured_tables/resync/bioentity_live_data/bioentity_live_data.py, which runs the
// same directBioentities query + BioentityLiveDataGraphQL fragment so backend resync matches this path.
export const BULK_BIOENTITY_LIVE_DATA_QUERY = gql`
  query BulkBioentityLiveDataQuery($inputs: [RegistryInput!]!) @skipAudit @engComponent(name: REGISTRY) {
    directBioentities(inputs: $inputs) {
      ...BioentityLiveDataGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BioentityLiveData').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<DirectLookupInfo>
): Promise<BulkBioentityLiveDataQuery['directBioentities']> {
  const {directBioentities} = await doQuerySafe<
    BulkBioentityLiveDataQuery,
    BulkBioentityLiveDataQueryVariables
  >(
    {query: BULK_BIOENTITY_LIVE_DATA_QUERY},
    {
      variables: {
        inputs: sampleLookupInfos.map((info) => ({
          id: info.idForLookup,
          customSyntaxName: info.customSyntaxName,
        })),
      },
      fetchPolicy: 'network-only',
    }
  );
  return directBioentities;
}

// Agent keep in sync: RegistrationRowUpdater in
// benchling/notebook/structured_tables/resync/row_updaters/registration.py
export const RegistrationTableRowUpdater: StructuredTableRowUpdater<
  RegisteredEntityInfo,
  RegistrationStructuredTableRowMetadata
> = {
  fetchModel(
    sampleLookupInfo: DirectLookupInfo
  ): Promise<BulkBioentityLiveDataQuery['directBioentities'][number]> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<DirectLookupInfo>
  ): Promise<BulkBioentityLiveDataQuery['directBioentities']> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache(sampleLookupInfo: DirectLookupInfo): RegisteredEntityInfo | null {
    return readFragmentFromCache(getFragmentWrapper('BioentityLiveData'), sampleLookupInfo.idForLookup);
  },

  updateCell: (
    model: RegisteredEntityInfo | InaccessibleInfoGraphQL,
    _: ColumnGroup,
    column: StructuredTableColumn
  ): LiveCellValue => {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }

    switch (column.columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
        return getTypedValueFromBioentitySummary({id: model.id, displayLabel: model.displayLabel});

      case TableColumnType.BIOENTITY_NAME:
        return toStringTypedValue(model.name);

      case TableColumnType.DNA_BASES:
      case TableColumnType.RNA_BASES:
        assert(
          model.__typename === 'SequenceNode',
          `Found a ${column.columnType} column in a non-sequence registration table.`
        );
        return toStringTypedValue(model.basesIfShowResidues || '');
      case TableColumnType.DNA_HELM:
      case TableColumnType.RNA_HELM:
        assert(
          model.__typename === 'SequenceNode',
          `Found a ${column.columnType} column in a non-sequence registration table.`
        );
        return toStringTypedValue(model.helmString || '');
      case TableColumnType.DNA_IDT:
      case TableColumnType.RNA_IDT:
        assert(
          model.__typename === 'SequenceNode',
          `Found a ${column.columnType} column in a non-sequence registration table.`
        );
        return toStringTypedValue(model.idtString || '');
      case TableColumnType.BASES:
        return toStringTypedValue(
          getBasesValueFromBioentitySummary(
            (column as BasesStructuredTableColumn).basesColumnConfig,
            model
          ) || ''
        );
      case TableColumnType.SEQUENCE_TOPOLOGY:
        return toStringTypedValue(getTopologyValueFromBioentitySummary(model) || '');
      case TableColumnType.SMILES:
        assert(
          model.__typename === 'MoleculeNode',
          `Found a ${column.columnType} column in a non-molecule registration table.`
        );
        return toStringTypedValue(model.canonicalSmiles || '');

      case TableColumnType.ALIAS: {
        return {
          type: BaseTypedValueType.ARRAY,
          rawValue: RegistryIdentifierUtil.getNonRegistryIdentifierAliasInfos(model).map(({alias}) => ({
            type: BaseTypedValueType.STRING,
            rawValue: alias,
          })),
        };
      }

      case TableColumnType.TAG_VALUE: {
        const matchingTag = model.tagsJson.find(
          (tag: Tag) => isSchemaTag(tag) && tag.tagSchemaField.apiId === column.schemaFieldApiId
        );
        return matchingTag != null ? convertTagToTypedValue(matchingTag) : 'NO_VALUE';
      }

      case TableColumnType.COMPLEX_POLYMER_COMPONENT: {
        const matchingTag = model.tagsJson.find(
          (tag: Tag): tag is SchemaTag =>
            isSchemaTag(tag) && tag.tagSchemaField.apiId === column.schemaFieldApiId
        );
        if (matchingTag == null) {
          return 'NO_VALUE';
        }
        const targetFiles = matchingTag.targetFiles ?? [];
        return {
          type: BaseTypedValueType.ARRAY,
          rawValue: targetFiles
            .map((file) => getTypedValueFromBioentitySummary({id: file.id, displayLabel: file.name}))
            .filter((v): v is BioentityTypedValue => v != null),
        };
      }

      default:
        break;
    }

    return 'NO_VALUE';
  },

  updateRow(
    model: RegisteredEntityInfo,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<RegistrationStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<RegistrationStructuredTableRowMetadata> {
    // Passed to updateCellWithLiveValues to handle the special cased behavior surrounding the text field
    // in RegistrationTableRowUpdater.
    // Agent keep in sync: _registration_raw_text_value in
    // benchling/notebook/structured_tables/resync/row_updaters/registration.py
    const setRawTextValue = (lcv: TypedValue): string =>
      lcv?.type === BaseTypedValueType.STRING || lcv?.type === FieldType.TEXT ? formatTypedValue(lcv) : '';
    return {
      ...row,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell, {setRawTextValue}),
      lastSyncedArchivePurpose: model.archiveRecord?.purpose ?? null,
    };
  },
};
